import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Globe, ArrowLeft, Mail, CheckCircle2, Sparkles, Users, Zap, Share, MoreVertical, Plus, Smartphone, Sunrise, Sun, CloudSun, Sunset, Moon, Clock, XCircle, Brain, TrendingUp, Lightbulb } from "lucide-react";
import { DemoPersona } from "../services/storage";
import { getGlobalStats, GlobalStats } from "../services/api";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { isSupabaseConfigured } from "../services/supabaseClient";
import { useTheme } from "../hooks/useTheme";
import ThemeToggle from "../components/ThemeToggle";

type FormMode = "signin" | "signup" | "recovery";

// Animated Hourglass with Flames component for loading
const AnimatedHourglass: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <motion.div
    className="relative inline-flex items-center justify-center"
    animate={{ rotate: 360 }}
    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="text-white">
      <path 
        d="M6 2h12v4l-4 4 4 4v4H6v-4l4-4-4-4V2z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="rgb(var(--on-fill) / 0.2)"
      />
    </svg>
  </motion.div>
);

// Optimized Dust Particle - Grok-style crisp, slow drift
const DustParticle: React.FC<{ 
  x: number; 
  y: number; 
  size: number; 
  opacity: number;
  duration: number;
  driftX: number;
  driftY: number;
}> = ({ x, y, size, opacity, duration, driftX, driftY }) => {
  return (
    <motion.div
      className="absolute rounded-full bg-white pointer-events-none"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
        opacity: opacity,
      }}
      animate={{
        x: [0, driftX, 0],
        y: [0, driftY, 0],
      }}
      transition={{
        duration: duration,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  );
};

// Demo prayer data for Section 2 animation
const DEMO_PRAYERS = [
  { id: 'fajr', name: 'Fajr', nameAr: 'الفجر', icon: Sunrise },
  { id: 'dhuhr', name: 'Dhuhr', nameAr: 'الظهر', icon: Sun },
  { id: 'asr', name: 'Asr', nameAr: 'العصر', icon: CloudSun },
  { id: 'maghrib', name: 'Maghrib', nameAr: 'المغرب', icon: Sunset },
  { id: 'isha', name: 'Isha', nameAr: 'العشاء', icon: Moon },
];

// Footer easter egg endings library
const FOOTER_ENDINGS = {
  en: [
    "the Ummah", "the Hereafter", "your awakening", "your clarity", "your heart",
    "your accountability", "your growth", "your sincerity", "your journey", "your nearness to God",
    "your discipline", "your peace", "your inner light", "your salvation", "your purpose",
    "your resilience", "your soul", "your record", "your mindful days", "your intentions",
    "your truth", "the seekers of goodness", "the sincere ones", "the mindful", "the disciplined",
    "the ones striving", "the ones returning", "the ones who rise after falling", "the ones who refuse heedlessness", "the ones who walk with purpose",
    "the ones who guard their deeds", "a clearer heart", "a stronger soul", "self-reflection", "the courageous",
    "the ones who change", "the ones who begin today", "the ones who choose the right path", "those who seek the unseen reward", "a better tomorrow",
    "a better you", "conscious living", "spiritual growth", "inner honesty", "moral clarity",
    "your higher calling", "your final moment", "the Day of Reckoning", "overcoming the self", "defeating the whisper", "walking toward the Light"
  ],
  ar: [
    "للأمة", "للآخرة", "لنجاتك", "لطمأنينة قلبك", "لمحاسبة النفس",
    "لليقظة", "لتهذيب النفس", "لقربك من الله", "لاستقامة الطريق", "للروح التي تبحث عن نور",
    "لمجاهدة الهوى", "لصفاء السريرة", "لمن أراد الحق", "لمن يسعى للازدياد", "لمن يراقب قلبه",
    "لحياة أعمق", "لميزانك", "ليوم لا ينفع فيه مال ولا بنون", "لصلاح السر والعلن", "لزيادة بصيرتك",
    "للنفس اللوَّامة", "للذاكرين الله كثيرًا", "لمن يخشى مقام ربه", "لمن أراد التغيير", "لمن يبحث عن النجاة",
    "لمن يخطو أول خطوة", "لقلوب تستحي من التقصير", "لقلوب تحب الله", "لمن يريد أن يبدأ", "لمن يريد ألا يغفل",
    "ليوم الحسرة والندامة", "لمستقبلٍ لا ينطفئ نوره", "لمن يراجع أعماله", "لمن يفرح بالطاعة", "لمن يخاف من الغفلة",
    "لمن يصلح عيوبه", "لمن يتوب ويعود", "لمن يعرف قدر نفسه", "لمن يحب النجاة", "لمن يعمل بصمت",
    "لرحلة الاستقامة", "لطهارة الداخل", "لمن أرهقه البعد", "لمن أراد القرب", "لمن يطلب الهداية",
    "لمن يعمل للقاء الله", "لقلوب تخشى سوء الخاتمة", "لمن يختار طريق الحق", "لتجديد همّتك", "لصيانة أعمالك", "لمن يكتب كتابه بيده"
  ]
};

// Quality levels matching real PrayerCard
const QUALITY_LEVELS = [
  { id: 0, icon: Sparkles, color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/40' },
  { id: 1, icon: Users, color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/40' },
  { id: 2, icon: Clock, color: 'text-orange-400', bg: 'bg-orange-500/20', border: 'border-orange-500/40' },
  { id: 3, icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/40' },
];

// Demo Prayer Card Component (matches real PrayerCard styling)
const DemoPrayerCard: React.FC<{
  prayer: typeof DEMO_PRAYERS[0];
  isArabic: boolean;
  isLogging: boolean;
  isLogged: boolean;
  loggedQuality: number | null;
  activeButton: number | null;
}> = ({ prayer, isArabic, isLogging, isLogged, loggedQuality, activeButton }) => {
  const Icon = prayer.icon;
  const activeQuality = activeButton !== null ? QUALITY_LEVELS[activeButton] : null;
  const finalQuality = loggedQuality !== null ? QUALITY_LEVELS[loggedQuality] : null;
  
  // Determine which quality to show (logging animation or final logged state)
  const displayQuality = isLogged ? finalQuality : (isLogging ? activeQuality : null);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 1, scale: 1 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 0.3, 
        ease: "easeOut",
        layout: { duration: 0.3, ease: "easeInOut", type: "tween" }
      }}
      className={`
        relative overflow-hidden rounded-2xl mb-2
        flex items-stretch
        bg-surface
        border transition-all duration-300
        ${displayQuality ? displayQuality.border : 'border-white/10'}
        ${displayQuality ? 'shadow-lg shadow-emerald-500/10' : ''}
      `}
    >
      {/* Glow effect when logging or logged */}
      {displayQuality && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`absolute inset-0 ${displayQuality.bg} pointer-events-none`}
        />
      )}
      
      {/* Left side - Icon & Name */}
      <div className="flex items-center px-4 py-3 flex-1 min-w-0">
        <div className={`w-9 h-9 flex items-center justify-center mr-3 transition-colors ${displayQuality ? displayQuality.color : 'text-emerald-500/70'}`}>
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <h3 className={`font-bold text-sm truncate ${displayQuality ? displayQuality.color : 'text-ink-primary'}`}>
          {isArabic ? prayer.nameAr : prayer.name}
        </h3>
      </div>
      
      {/* Right side - Quality Buttons */}
      <div className="flex items-center gap-1 px-2 py-2 border-l border-white/5">
        {QUALITY_LEVELS.map((level) => {
          const LevelIcon = level.icon;
          const isActive = activeButton === level.id || (isLogged && loggedQuality === level.id);
          return (
            <div
              key={level.id}
              className={`
                w-8 h-8 rounded-lg flex items-center justify-center
                transition-all duration-200
                ${isActive 
                  ? `${level.bg} ${level.color} scale-110 ${level.border} border` 
                  : 'text-ink-tertiary bg-white/5'
                }
              `}
            >
              <LevelIcon size={14} />
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

// Animated Counter Component
const AnimatedCounter: React.FC<{ 
  value: number; 
  duration?: number;
  suffix?: string;
}> = ({ value, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  useEffect(() => {
    if (!isInView || value === 0) return;
    
    let startTime: number;
    let animationFrame: number;
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      // Easing function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * value));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value, duration]);
  
  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

// Optimized particles: 70 crisp dots with slow infinite drift (Grok-style)
// Defined OUTSIDE component to prevent regeneration on every render
const DUST_PARTICLES = Array.from({ length: 70 }, () => ({
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: 1 + Math.random() * 2, // 1px to 3px - crisp, no blur
  opacity: 0.15 + Math.random() * 0.25, // 0.15 to 0.4 - subtle
  duration: 20 + Math.random() * 20, // 20s to 40s - very slow drift
  driftX: (Math.random() - 0.5) * 80, // -40 to 40px horizontal drift
  driftY: (Math.random() - 0.5) * 80, // -40 to 40px vertical drift
}));

const Login: React.FC = () => {
  const { signInWithEmail, signUpWithEmail, startDemo, resetPasswordForEmail } = useAuth();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [language, setLanguage] = useState<"en" | "ar">("ar");
  const [mode, setMode] = useState<FormMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  
  // Demo animation state for Section 2
  const [loggedPrayers, setLoggedPrayers] = useState<Record<string, number>>({}); // prayerId -> qualityIndex
  const [currentlyLogging, setCurrentlyLogging] = useState<string | null>(null);
  const [activeButtons, setActiveButtons] = useState<Record<string, number>>({});
  
  // Global stats for Section 5
  const [globalStats, setGlobalStats] = useState<GlobalStats>({ totalHabitsLogged: 0, totalAiInsights: 0 });
  
  // Footer easter egg state
  const [footerEndingIndex, setFooterEndingIndex] = useState(0);

  const isArabic = language === "ar";
  
  // Get current footer ending based on language
  const currentFooterEnding = isArabic 
    ? FOOTER_ENDINGS.ar[footerEndingIndex]
    : FOOTER_ENDINGS.en[footerEndingIndex];
  
  const handleFooterClick = () => {
    const endings = isArabic ? FOOTER_ENDINGS.ar : FOOTER_ENDINGS.en;
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * endings.length);
    } while (newIndex === footerEndingIndex); // Ensure it's different
    setFooterEndingIndex(newIndex);
  };
  
  // Fetch global stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      const stats = await getGlobalStats();
      setGlobalStats(stats);
    };
    fetchStats();
  }, []);
  
  // Auto-animate the prayer demo with random quality selection
  useEffect(() => {
    const unloggedPrayers = DEMO_PRAYERS.filter(p => !(p.id in loggedPrayers));
    
    if (unloggedPrayers.length === 0) {
      // All prayers logged, reset after showing completion message
      const resetTimer = setTimeout(() => {
        setLoggedPrayers({});
        setCurrentlyLogging(null);
        setActiveButtons({});
      }, 3000);
      return () => clearTimeout(resetTimer);
    }
    
    // Start logging the next prayer after a delay
    const logTimer = setTimeout(() => {
      const nextPrayer = unloggedPrayers[0];
      if (nextPrayer) {
        // Pick a random quality level (0-3)
        const randomQuality = Math.floor(Math.random() * 4);
        
        // First highlight the button
        setActiveButtons(prev => ({ ...prev, [nextPrayer.id]: randomQuality }));
        
        // Then start the logging animation
        setTimeout(() => {
          setCurrentlyLogging(nextPrayer.id);
          
          // After animation, mark as logged with quality level
          setTimeout(() => {
            setLoggedPrayers(prev => ({ ...prev, [nextPrayer.id]: randomQuality }));
            setCurrentlyLogging(null);
          }, 500);
        }, 400);
      }
    }, 1500);
    
    return () => clearTimeout(logTimer);
  }, [loggedPrayers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: authError } =
      mode === "signin"
        ? await signInWithEmail(email, password)
        : await signUpWithEmail(email, password);

    setLoading(false);

    if (authError) {
      setError(authError.message);
    }
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: resetError } = await resetPasswordForEmail(email);

    setLoading(false);

    if (resetError) {
      setError(resetError.message);
    } else {
      setRecoverySuccess(true);
    }
  };

  const handleDemo = (persona: DemoPersona) => {
    startDemo(persona);
  };

  const switchToRecovery = () => {
    setMode("recovery");
    setError(null);
    setRecoverySuccess(false);
  };

  const switchToLogin = () => {
    setMode("signin");
    setError(null);
    setRecoverySuccess(false);
  };

  const personas = [
    {
      id: "devout" as DemoPersona,
      nameEn: "The Worshiper",
      nameAr: "سابق بالخيرات",
      descEn: "95% consistency",
      descAr: "٩٥٪ التزام",
      icon: Sparkles,
      gradient: "from-emerald-500/20 to-teal-500/10",
      border: "border-emerald-500/40",
      iconColor: "text-emerald-400",
    },
    {
      id: "intermediate" as DemoPersona,
      nameEn: "Half & Half",
      nameAr: "اللي نص نص",
      descEn: "75% consistency",
      descAr: "٧٥٪ التزام",
      icon: Users,
      gradient: "from-amber-500/20 to-orange-500/10",
      border: "border-amber-500/40",
      iconColor: "text-amber-400",
    },
    {
      id: "beginner" as DemoPersona,
      nameEn: "The Careless",
      nameAr: "لاهية قلوبهم",
      descEn: "30% consistency",
      descAr: "٣٠٪ التزام",
      icon: Zap,
      gradient: "from-slate-500/20 to-slate-600/10",
      border: "border-slate-500/40",
      iconColor: "text-slate-400",
    },
  ];

  const [installTab, setInstallTab] = useState<'iphone' | 'android'>('iphone');

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col relative overflow-y-auto" dir={isArabic ? "rtl" : "ltr"}>
      {/* Animated Background Effects - z-index 0 to stay behind content cards */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Gradient orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-600/5 rounded-full blur-[150px]" />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(${palette.takbirah.bright}4D 1px, transparent 1px), linear-gradient(90deg, ${palette.takbirah.bright}4D 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Optimized Grok-style particles: crisp, slow, always-on */}
        {DUST_PARTICLES.map((p, i) => (
          <DustParticle key={`dust-${i}`} {...p} />
        ))}
      </div>

      {/* Top Bar */}
      <header className="w-full px-6 py-5 flex items-center justify-between relative z-10">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2.5"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <Sparkles className="text-white" size={18} />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">حَسِيب</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-1"
        >
          <button
            onClick={() => setLanguage(language === "en" ? "ar" : "en")}
            className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-ink-primary hover:bg-white/5 rounded-full transition-all text-sm"
          >
            <Globe size={16} />
            {language === "en" ? "العربية" : "English"}
          </button>
          {/* Light/dark normally follows the visitor's clock; this pins it. */}
          <ThemeToggle
            labelLight={isArabic ? "الوضع الفاتح" : "Switch to light mode"}
            labelDark={isArabic ? "الوضع الداكن" : "Switch to dark mode"}
          />
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center px-6 pt-8 pb-16 relative z-10">
        <div className="w-full max-w-md">
          
          {/* Hero */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl font-black text-white mb-4 tracking-tight leading-tight">
              {isArabic ? (
                <>ابدأ <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">رحلتك</span></>
              ) : (
                <>Start Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">Journey</span></>
              )}
            </h1>
            <p className="text-ink-secondary text-lg leading-relaxed">
              {isArabic 
                ? "تتبع عباداتك • حسّن نفسك • اقترب من الله" 
                : "Track worship • Improve yourself • Get closer to Allah"}
            </p>
          </motion.div>

          {/* Section 1: The Why - Spiritual Anchor */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-16 text-center"
          >
            {/* Opening Text - The Book Metaphor */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="mb-10"
            >
              <p className="text-ink-secondary text-base leading-loose font-regular" style={{ fontFamily: 'Georgia, serif' }}>
                {isArabic ? (
                  <>
                    هناك كتابٌ معلَّقٌ على كتفك،
                    <br />
                    يُزاد فيه كلَّ يومٍ ما كُتِب من غفلةٍ أو يقظة،
                    <br />
                    من تقصيرٍ أو مجاهدة…
                    <br /><br />
                    كتابٌ لم تُقلَّب صفحاته بعد،
                    <br />
                    ولكنك ستقف عليه يومًا لا مفر منه،
                    <br />
                    يوم يُقال لك:
                  </>
                ) : (
                  <>
                    There is a book upon your shoulder,
                    <br />
                    A record that grows each day with moments
                    <br />
                    of heedlessness or awareness, neglect or striving.
                    <br /><br />
                    A book whose pages you have not yet seen…
                    <br />
                    But one day you will stand before it,
                    <br />
                    And it will be said to you:
                  </>
                )}
              </p>
            </motion.div>

            {/* The Verse Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="relative mx-auto mb-10"
            >
              {/* The artwork is white-on-transparent, so the card carries the
                  takbirah gradient in BOTH modes — white on a gradient fill is
                  exactly where the brand allows it. */}
              <div className="relative p-6 rounded-2xl bg-gradient-to-br from-takbirah-deep to-takbirah-bright border border-takbirah-bright/40 shadow-2xl">
                <img 
                  src="/verse.png" 
                  alt={isArabic ? "آية قرآنية" : "Quranic Verse"}
                  className="relative w-full max-w-[280px] mx-auto rounded-lg"
                />
              </div>
            </motion.div>

            {/* Closing Text - Call to Awakening */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
            >
              <p className="text-ink-secondary text-base leading-loose font-regular" style={{ fontFamily: 'Georgia, serif' }}>
                {isArabic ? (
                  <>
                    فإن لم تُحاسِب نفسك اليوم، فمتى ينفعك الحساب؟
                    <br />
                    قِف مع نفسك وقفة حق…
                    <br /><br /><br />
                    فالطريق إلى النجاة يبدأ من اليقظة،
                    <br />
                    ومن أدرك نفسه اليوم، نجا غدًا.
                  </>
                ) : (
                  <>
                    If you do not hold your soul accountable now,
                    <br />
                    then when will accountability benefit you?
                    <br /><br />
                    Stand with yourself in truth…
                    <br />
                    for the path to salvation begins with awakening.
                    <br />
                    Whoever grasps his soul today will be saved tomorrow.
                  </>
                )}
              </p>
            </motion.div>

            {/* Decorative Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="mt-12 flex items-center justify-center gap-3"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-emerald-500/50" />
              <Sparkles size={16} className="text-emerald-500/50" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-emerald-500/50" />
            </motion.div>
          </motion.section>

          {/* Section 2: The Plan - Interactive Demo */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-6"
            >
              <h2 className="text-3xl font-black text-white mb-2">
                {isArabic ? 'الخطة' : 'The Plan'}
              </h2>
              <p className="text-ink-secondary text-base">
                {isArabic 
                  ? 'سجّل صلواتك، ارتقِ بجودتها' 
                  : 'Log your prayers, elevate your quality'}
              </p>
            </motion.div>

            {/* Interactive Prayer Demo - Fixed Height, Transparent Background */}
            <div className="relative h-[360px] overflow-hidden">
              {/* Prayer Cards */}
              <div className="relative">
                <AnimatePresence mode="popLayout">
                  {DEMO_PRAYERS.map((prayer) => (
                    <DemoPrayerCard
                      key={prayer.id}
                      prayer={prayer}
                      isArabic={isArabic}
                      isLogging={currentlyLogging === prayer.id}
                      isLogged={prayer.id in loggedPrayers}
                      loggedQuality={loggedPrayers[prayer.id] ?? null}
                      activeButton={activeButtons[prayer.id] ?? null}
                    />
                  ))}
                </AnimatePresence>
              </div>
              
              {/* Completion Message */}
              <AnimatePresence>
                {Object.keys(loggedPrayers).length === 5 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0 flex flex-col items-center justify-center text-center bg-slate-950/80 backdrop-blur-sm"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 0.8 }}
                      className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4 border border-emerald-500/30"
                    >
                      <Sparkles size={28} className="text-emerald-400" />
                    </motion.div>
                    <p 
                      className="text-white font-bold text-xl leading-relaxed"
                      style={{ textShadow: '0 2px 20px rgb(var(--scrim) / 0.9), 0 4px 40px rgb(var(--scrim) / 0.8)' }}
                    >
                      {isArabic ? (
                        <>للصلوات. للرواتب.<br />لكل أعمالك.</>
                      ) : (
                        <>For Prayers. For Rawatib.<br />For all of your work.</>
                      )}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Decorative Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex items-center justify-center gap-3"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-emerald-500/50" />
              <Sparkles size={16} className="text-emerald-500/50" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-emerald-500/50" />
            </motion.div>
          </motion.section>

          {/* Section 3: The Results - Analytics Carousel */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-6"
            >
              <h2 className="text-3xl font-black text-white mb-2">
                {isArabic ? 'النتائج' : 'The Results'}
              </h2>
              <p className="text-ink-secondary text-base">
                {isArabic 
                  ? 'تحليلات ورؤى لتتبع تقدمك' 
                  : 'Analytics & insights to track your progress'}
              </p>
            </motion.div>

            {/* Horizontal Carousel */}
            <div className="overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
              <div className="flex gap-4 w-max pb-4">
                
                {/* Card 1: Consolidated Prayer Analytics */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="snap-start w-[280px] shrink-0 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5"
                >
                  {/* Card Explanation - 25% of card */}
                  <div className="mb-4 pb-3 border-b border-white/[0.06]">
                    <h3 className="text-white font-bold text-sm mb-1.5">
                      {isArabic ? 'تحليل الصلوات' : 'Prayer Analytics'}
                    </h3>
                    <p className="text-ink-secondary text-xs leading-relaxed">
                      {isArabic 
                        ? 'تحليل شامل لجودة صلواتك الخمس اليومية. تتبع نسبة التكبيرة والجماعة والفائتة.'
                        : 'Full breakdown of your 5 daily prayers. Track Takbirah rate, Jamaa, and missed prayers.'}
                    </p>
                  </div>
                  
                  {/* Donut Chart + Stats - Centered Layout */}
                  <div className="flex flex-col items-center">
                    {/* CSS Donut Chart - Larger */}
                    <div className="relative w-28 h-28 mb-4">
                      <svg viewBox="0 0 36 36" className="w-full h-full rotate-[-90deg]">
                        <circle cx="18" cy="18" r="14" fill="none" stroke={palette.track} strokeWidth="3.5" />
                        <circle cx="18" cy="18" r="14" fill="none" stroke={palette.takbirah.bright} strokeWidth="3.5" 
                          strokeDasharray="60 100" strokeLinecap="butt" />
                        <circle cx="18" cy="18" r="14" fill="none" stroke={palette.jamaa.bright} strokeWidth="3.5" 
                          strokeDasharray="13 100" strokeDashoffset="-60" strokeLinecap="butt" />
                        <circle cx="18" cy="18" r="14" fill="none" stroke={palette.onTime.bright} strokeWidth="3.5" 
                          strokeDasharray="9 100" strokeDashoffset="-73" strokeLinecap="butt" />
                        <circle cx="18" cy="18" r="14" fill="none" stroke={palette.missed.bright} strokeWidth="3.5" 
                          strokeDasharray="6 100" strokeDashoffset="-82" strokeLinecap="butt" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-black text-amber-400">68%</span>
                        <span className="text-[8px] text-gray-500">{isArabic ? 'تكبيرة' : 'Takbirah'}</span>
                      </div>
                    </div>
                    
                    {/* Quality Breakdown - Horizontal */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 w-full">
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                        <span className="text-gray-400">{isArabic ? 'تكبيرة' : 'Takbirah'}</span>
                        <span className="font-bold text-emerald-400 ml-auto">68%</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 shrink-0" />
                        <span className="text-gray-400">{isArabic ? 'جماعة' : 'Jamaa'}</span>
                        <span className="font-bold text-yellow-400 ml-auto">15%</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0" />
                        <span className="text-gray-400">{isArabic ? 'في الوقت' : 'On Time'}</span>
                        <span className="font-bold text-orange-400 ml-auto">10%</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                        <span className="text-gray-400">{isArabic ? 'فائتة' : 'Missed'}</span>
                        <span className="font-bold text-red-400 ml-auto">7%</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Obstacles */}
                  <div className="mt-4 pt-3 border-t border-white/[0.06]">
                    <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">
                      {isArabic ? 'أهم العوائق' : 'Top Obstacles'}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 bg-red-500/10 text-red-400 text-[9px] rounded-full border border-red-500/20">
                        {isArabic ? 'النوم' : 'Sleep'}
                      </span>
                      <span className="px-2 py-1 bg-orange-500/10 text-orange-400 text-[9px] rounded-full border border-orange-500/20">
                        {isArabic ? 'العمل' : 'Work'}
                      </span>
                      <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-[9px] rounded-full border border-yellow-500/20">
                        {isArabic ? 'الكسل' : 'Laziness'}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Card 2: Monthly View */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="snap-start w-[280px] shrink-0 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5"
                >
                  {/* Card Explanation - 25% of card */}
                  <div className="mb-4 pb-3 border-b border-white/[0.06]">
                    <h3 className="text-white font-bold text-sm mb-1.5">
                      {isArabic ? 'العرض الشهري' : 'Monthly View'}
                    </h3>
                    <p className="text-ink-secondary text-xs leading-relaxed">
                      {isArabic 
                        ? 'كل حلقة تمثل يوم كامل. الألوان تدل على جودة كل صلاة من الخمس.'
                        : 'Each ring represents one day. Colors show the quality of each of the 5 prayers.'}
                    </p>
                  </div>
                  
                  {/* Mini Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day headers */}
                    {['S','M','T','W','T','F','S'].map((d, i) => (
                      <div key={i} className="text-[7px] text-gray-500 text-center font-bold mb-1">{d}</div>
                    ))}
                    {/* Empty cells for offset */}
                    {[...Array(3)].map((_, i) => <div key={`empty-${i}`} />)}
                    {/* Day rings - 28 days with varied quality colors */}
                    {[...Array(28)].map((_, i) => {
                      const dayNum = i + 1;
                      // Quality patterns: 0=Takbirah(green), 1=Jamaa(yellow), 2=OnTime(orange), 3=Missed(red), 4=NotLogged(gray)
                      // Ladder order: takbirah, jamaa, onTime, missed, not-logged
                      const qualityColors = [
                        palette.takbirah.bright,
                        palette.jamaa.bright,
                        palette.onTime.bright,
                        palette.missed.bright,
                        palette.track,
                      ];
                      // Simulated data per day - 5 prayers with different qualities
                      const dayPatterns = [
                        [0,0,1,0,0], [0,1,0,1,0], [0,0,0,2,0], [1,0,0,0,1], [0,0,0,0,0],
                        [0,1,1,0,0], [2,0,0,3,0], [0,0,0,0,0], [3,3,4,4,4], [0,0,0,0,0],
                        [0,1,0,0,1], [0,0,2,0,0], [1,1,0,0,0], [0,0,0,1,0], [0,0,0,0,0],
                        [2,0,0,0,2], [0,0,0,0,0], [0,1,0,1,0], [0,0,0,0,0], [0,2,0,0,1],
                        [0,0,0,0,0], [1,0,0,0,0], [0,0,1,0,0], [0,0,0,0,0], [3,0,0,2,0],
                        [0,0,0,0,0], [0,1,0,0,0], [0,0,0,0,0],
                      ];
                      const qualities = dayPatterns[i] || [4,4,4,4,4];
                      return (
                        <div key={dayNum} className="relative w-full aspect-square">
                          <svg viewBox="0 0 40 40" className="w-full h-full rotate-[-90deg]">
                            {qualities.map((q, j) => {
                              const startAngle = j * 72;
                              const endAngle = startAngle + 67;
                              const startRad = (startAngle * Math.PI) / 180;
                              const endRad = (endAngle * Math.PI) / 180;
                              const r = 15, cx = 20, cy = 20;
                              const x1 = cx + r * Math.cos(startRad);
                              const y1 = cy + r * Math.sin(startRad);
                              const x2 = cx + r * Math.cos(endRad);
                              const y2 = cy + r * Math.sin(endRad);
                              return (
                                <path 
                                  key={j} 
                                  d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
                                  fill="none" 
                                  stroke={qualityColors[q]} 
                                  strokeWidth={3.5} 
                                  strokeLinecap="round"
                                />
                              );
                            })}
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[7px] text-gray-500 font-bold">
                            {dayNum}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Card 3: Growth Table */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="snap-start w-[280px] shrink-0 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5"
                >
                  {/* Card Explanation - 25% of card */}
                  <div className="mb-4 pb-3 border-b border-white/[0.06]">
                    <h3 className="text-white font-bold text-sm mb-1.5">
                      {isArabic ? 'جدول النمو' : 'Growth Table'}
                    </h3>
                    <p className="text-ink-secondary text-xs leading-relaxed">
                      {isArabic 
                        ? 'تتبع تقدمك أسبوعياً وشهرياً وربع سنوياً وسنوياً. الأرقام الخضراء تعني تحسن.'
                        : 'Track progress weekly, monthly, quarterly & yearly. Green numbers mean improvement.'}
                    </p>
                  </div>
                  
                  {/* Growth Table */}
                  <div className="bg-white/[0.02] rounded-lg border border-white/[0.06] overflow-hidden">
                    {/* Header */}
                    <div className="grid grid-cols-5 bg-white/[0.03] p-2.5 text-[8px] font-bold text-gray-400 uppercase text-center">
                      <div className="text-start">{isArabic ? 'صلاة' : 'Prayer'}</div>
                      <div>WoW</div><div>MoM</div><div>QoQ</div><div>YoY</div>
                    </div>
                    {/* Rows */}
                    {[
                      { name: isArabic ? 'الفجر' : 'Fajr', data: ['+2', '+5', '+12', '+18'] },
                      { name: isArabic ? 'الظهر' : 'Dhuhr', data: ['+1', '+3', '+8', '+15'] },
                      { name: isArabic ? 'العصر' : 'Asr', data: ['-1', '+2', '+6', '+10'] },
                      { name: isArabic ? 'المغرب' : 'Maghrib', data: ['+3', '+4', '+9', '+14'] },
                      { name: isArabic ? 'العشاء' : 'Isha', data: ['0', '+1', '+4', '+8'] },
                    ].map((row, i) => (
                      <div key={i} className="grid grid-cols-5 border-t border-white/[0.04] p-2.5 text-center items-center">
                        <div className="text-start text-[9px] font-medium text-ink-primary">{row.name}</div>
                        {row.data.map((val, j) => {
                          const isPos = val.startsWith('+');
                          const isZero = val === '0';
                          return (
                            <div key={j} className={`text-[9px] font-bold ${isZero ? 'text-gray-500' : isPos ? 'text-green-400' : 'text-red-400'}`}>
                              {val}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Card 4: Rawatib View */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="snap-start w-[280px] shrink-0 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5"
                >
                  {/* Card Explanation - 25% of card */}
                  <div className="mb-4 pb-3 border-b border-white/[0.06]">
                    <h3 className="text-white font-bold text-sm mb-1.5">
                      {isArabic ? 'تتبع الرواتب' : 'Rawatib Tracker'}
                    </h3>
                    <p className="text-ink-secondary text-xs leading-relaxed">
                      {isArabic 
                        ? 'كل بيت يمثل ٦ سنن راتبة. الأزرق يعني جزئي، الأخضر يعني مكتمل.'
                        : 'Each house = 6 Sunnah prayers. Blue means partial, green means complete.'}
                    </p>
                  </div>
                  
                  {/* Mini Rawatib Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day headers */}
                    {['S','M','T','W','T','F','S'].map((d, i) => (
                      <div key={i} className="text-[7px] text-gray-500 text-center font-bold mb-1">{d}</div>
                    ))}
                    {/* Empty cells */}
                    {[...Array(3)].map((_, i) => <div key={`empty-r-${i}`} />)}
                    {/* House shapes - 28 days */}
                    {[...Array(28)].map((_, i) => {
                      const dayNum = i + 1;
                      // Simulated: ~70% completion patterns
                      const completedSegments = [6,4,5,3,6,5,2,6,4,5,1,6,3,4,6,5,4,6,0,5,4,6,3,5,6,4,2,6][i] || 0;
                      const allComplete = completedSegments === 6;
                      const segments = [
                        'M 20,6 L 6,18',   // roof-left
                        'M 20,6 L 34,18',  // roof-right
                        'M 6,18 L 9,34',   // wall-left
                        'M 34,18 L 31,34', // wall-right
                        'M 9,34 L 20,34',  // floor-left
                        'M 20,34 L 31,34', // floor-right
                      ];
                      return (
                        <div key={dayNum} className="relative w-full aspect-square">
                          <svg viewBox="0 0 40 40" className="w-full h-full">
                            {/* Base house */}
                            {segments.map((d, j) => (
                              <path key={`base-${j}`} d={d} fill="none" stroke={palette.track} strokeWidth={2.5} strokeLinecap="round" />
                            ))}
                            {/* Active segments */}
                            {segments.slice(0, completedSegments).map((d, j) => (
                              <path key={`active-${j}`} d={d} fill="none" stroke={allComplete ? palette.takbirah.bright : palette.jamaa.bright} strokeWidth={3} strokeLinecap="round" />
                            ))}
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[7px] text-gray-500 font-bold mt-1">
                            {dayNum}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
                
              </div>
            </div>

            {/* Scroll hint */}
            <p className="text-center text-ink-tertiary text-[10px] mt-2">
              {isArabic ? '← اسحب لرؤية المزيد →' : '← Swipe to see more →'}
            </p>

            {/* Decorative Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex items-center justify-center gap-3"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-emerald-500/50" />
              <Sparkles size={16} className="text-emerald-500/50" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-emerald-500/50" />
            </motion.div>
          </motion.section>

          {/* Section 4: The Intelligence - AI Coaching */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-6"
            >
              <h2 className="text-3xl font-black text-white mb-2">
                {isArabic ? 'الذكاء' : 'The Intelligence'}
              </h2>
              <p className="text-ink-secondary text-base">
                {isArabic 
                  ? 'إخلاص وتحسين مستمر' 
                  : 'Sincerity & Continuous Improvement'}
              </p>
            </motion.div>

            {/* AI Feature Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white/[0.03] backdrop-blur-xl border border-purple-500/20 rounded-2xl p-6 relative overflow-hidden"
            >
              {/* Purple glow background */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-violet-500/5 pointer-events-none" />
              
              {/* Content */}
              <div className="relative flex flex-col items-center text-center">
                {/* Glowing Brain Icon */}
                <motion.div
                  animate={{ 
                    boxShadow: [
                      `0 0 20px ${palette.takbirah.bright}4D`,
                      `0 0 40px ${palette.takbirah.bright}80`,
                      `0 0 20px ${palette.takbirah.bright}4D`
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-16 h-16 rounded-2xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center mb-5"
                >
                  <Brain size={32} className="text-purple-400" />
                </motion.div>
                
                {/* Main Text */}
                <p className="text-ink-primary text-base leading-relaxed mb-4">
                  {isArabic 
                    ? 'ذكاء اصطناعي يفهم نمطك الروحي. لا يكتفي بالعد، بل يوجهك.'
                    : "AI that understands your spiritual patterns. It doesn't just count; it coaches."}
                </p>
                
                {/* Example Insight Card */}
                <div className="w-full bg-white/[0.03] border border-purple-500/10 rounded-xl p-4 mt-2">
                  <p className="text-[10px] text-purple-400 uppercase tracking-wider font-bold mb-2">
                    {isArabic ? 'مثال على الرؤية' : 'Example Insight'}
                  </p>
                  <p className="text-ink-primary text-sm leading-relaxed italic">
                    {isArabic 
                      ? '"دائماً تضيع عنك فجر الجمعة، انتبه لها غداً."'
                      : '"There is a pattern of missing Fajr on Fridays. Pay attention, make sure to pray tomorrow."'}
                  </p>
                  <p className="text-purple-400/60 text-[10px] mt-2">
                    {isArabic ? '— يوم الخميس' : '— Thursday'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Decorative Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex items-center justify-center gap-3"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-purple-500/50" />
              <Brain size={16} className="text-purple-500/50" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-purple-500/50" />
            </motion.div>
          </motion.section>

          {/* Section 5: The Impact - Live Counters */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl font-black text-white mb-2">
                {isArabic ? 'الأثر' : 'The Impact'}
              </h2>
              <p className="text-ink-secondary text-base">
                {isArabic 
                  ? 'أرقام حقيقية من مجتمعنا' 
                  : 'Real numbers from our community'}
              </p>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Habits Logged */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white/[0.03] backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-5 text-center relative overflow-hidden"
              >
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent pointer-events-none" />
                
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
                    <TrendingUp size={24} className="text-emerald-400" />
                  </div>
                  <div className="text-4xl font-black text-white mb-1">
                    <AnimatedCounter 
                      value={globalStats.totalHabitsLogged} 
                      suffix={globalStats.totalHabitsLogged > 0 ? "+" : ""} 
                    />
                  </div>
                  <p className="text-ink-secondary text-sm">
                    {isArabic ? 'عبادة مسجلة' : 'Habits Logged'}
                  </p>
                </div>
              </motion.div>

              {/* AI Insights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white/[0.03] backdrop-blur-xl border border-purple-500/20 rounded-2xl p-5 text-center relative overflow-hidden"
              >
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-transparent pointer-events-none" />
                
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center mx-auto mb-3">
                    <Lightbulb size={24} className="text-purple-400" />
                  </div>
                  <div className="text-4xl font-black text-white mb-1">
                    <AnimatedCounter 
                      value={globalStats.totalAiInsights} 
                      suffix={globalStats.totalAiInsights > 0 ? "+" : ""} 
                    />
                  </div>
                  <p className="text-ink-secondary text-sm">
                    {isArabic ? 'نصيحة ذكية' : 'AI Insights'}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Decorative Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex items-center justify-center gap-3"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-emerald-500/50" />
              <Sparkles size={16} className="text-emerald-500/50" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-emerald-500/50" />
            </motion.div>
          </motion.section>

          {/* Section 6: Get Started - Download & Auth */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl font-black text-white mb-2">
                {isArabic ? 'ابدأ الآن' : 'Get Started'}
              </h2>
              <p className="text-ink-secondary text-base">
                {isArabic 
                  ? 'حمّل التطبيق أو سجّل دخولك' 
                  : 'Download the app or sign in'}
              </p>
            </motion.div>

            {/* App Store Buttons - Coming Soon */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex gap-3 justify-center mb-8"
            >
              {/* App Store Button */}
              <button
                onClick={() => {}}
                className="relative group flex items-center gap-2 px-5 py-3 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl hover:bg-white/[0.06] transition-all"
              >
                {/* Apple Icon */}
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[9px] text-ink-secondary leading-none">{isArabic ? 'قريباً على' : 'Coming soon on'}</p>
                  <p className="text-sm font-bold text-white leading-tight">App Store</p>
                </div>
                {/* Coming Soon Badge */}
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[8px] font-bold rounded-full border border-amber-500/30">
                  {isArabic ? 'قريباً' : 'SOON'}
                </span>
              </button>

              {/* Google Play Button */}
              <button
                onClick={() => {}}
                className="relative group flex items-center gap-2 px-5 py-3 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl hover:bg-white/[0.06] transition-all"
              >
                {/* Play Store Icon */}
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[9px] text-ink-secondary leading-none">{isArabic ? 'قريباً على' : 'Coming soon on'}</p>
                  <p className="text-sm font-bold text-white leading-tight">Google Play</p>
                </div>
                {/* Coming Soon Badge */}
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[8px] font-bold rounded-full border border-amber-500/30">
                  {isArabic ? 'قريباً' : 'SOON'}
                </span>
              </button>
            </motion.div>

            {/* Divider with "or" */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/[0.06]"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-slate-950 text-ink-tertiary text-xs uppercase tracking-widest font-medium">
                  {isArabic ? 'أو' : 'or'}
                </span>
              </div>
            </div>
          </motion.section>

          <AnimatePresence mode="wait">
            {mode === "recovery" ? (
              /* Recovery Mode */
              <motion.div
                key="recovery"
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <button
                  onClick={switchToLogin}
                  className={`flex items-center gap-2 text-ink-secondary hover:text-white transition-colors text-sm ${isArabic ? 'flex-row-reverse' : ''}`}
                >
                  <ArrowLeft size={16} className={isArabic ? 'rotate-180' : ''} />
                  {isArabic ? "العودة" : "Back"}
                </button>

                <div className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
                      <Mail className="text-emerald-400" size={24} />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-1">
                      {isArabic ? "إعادة تعيين كلمة المرور" : "Reset Password"}
                    </h2>
                    <p className="text-ink-tertiary text-sm">
                      {isArabic
                        ? "أدخل بريدك الإلكتروني"
                        : "Enter your email address"}
                    </p>
                  </div>

                  {recoverySuccess ? (
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center py-4"
                    >
                      <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="text-emerald-400" size={28} />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {isArabic ? "تحقق من بريدك!" : "Check Your Email!"}
                      </h3>
                      <p className="text-ink-tertiary text-sm mb-4">{email}</p>
                      <button
                        onClick={switchToLogin}
                        className="text-emerald-400 hover:text-emerald-300 text-sm font-medium"
                      >
                        {isArabic ? "العودة" : "Return to Login"}
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleRecovery} className="space-y-4">
                      <input
                        type="email"
                        placeholder={isArabic ? "البريد الإلكتروني" : "Email"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full px-4 py-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/20 focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.05] transition-all text-base ${isArabic ? 'text-right' : 'text-left'}`}
                        required
                        dir={isArabic ? "rtl" : "ltr"}
                      />
                      {error && (
                        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-center">
                          {error}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-on-fill rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {loading && <AnimatedHourglass size={18} />}
                        {isArabic ? "إرسال" : "Send Link"}
                      </button>
                    </form>
                  )}
                </div>
              </motion.div>
            ) : (
              /* Sign In / Sign Up Mode */
              <motion.div
                key="auth"
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                {!isSupabaseConfigured && (
                  <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs rounded-xl px-4 py-3 text-center">
                    {isArabic
                      ? "لم يتم إعداد Supabase بعد. استخدم وضع التجربة في الأسفل."
                      : "Supabase is not configured yet. Use demo mode below."}
                  </div>
                )}
                {/* Auth Card */}
                <div className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6">
                  {/* Tab Switcher */}
                  <div className="flex bg-white/[0.03] rounded-xl p-1 mb-6 border border-white/[0.04]">
                    <button
                      onClick={() => setMode("signin")}
                      className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all ${
                        mode === "signin"
                          ? "bg-gradient-to-r from-emerald-500/20 to-teal-500/10 text-emerald-400 border border-emerald-500/30"
                          : "text-ink-tertiary hover:text-ink-secondary"
                      }`}
                    >
                      {isArabic ? "تسجيل الدخول" : "Sign In"}
                    </button>
                    <button
                      onClick={() => setMode("signup")}
                      className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all ${
                        mode === "signup"
                          ? "bg-gradient-to-r from-emerald-500/20 to-teal-500/10 text-emerald-400 border border-emerald-500/30"
                          : "text-ink-tertiary hover:text-ink-secondary"
                      }`}
                    >
                      {isArabic ? "حساب جديد" : "Sign Up"}
                    </button>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="email"
                      placeholder={isArabic ? "البريد الإلكتروني" : "Email"}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full px-4 py-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/20 focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.05] transition-all text-base ${isArabic ? 'text-right' : 'text-left'}`}
                      required
                      dir={isArabic ? "rtl" : "ltr"}
                    />
                    <input
                      type="password"
                      placeholder={isArabic ? "كلمة المرور" : "Password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full px-4 py-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/20 focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.05] transition-all text-base ${isArabic ? 'text-right' : 'text-left'}`}
                      required
                      dir={isArabic ? "rtl" : "ltr"}
                    />

                    {mode === "signin" && (
                      <div className={isArabic ? "text-left" : "text-right"}>
                        <button
                          type="button"
                          onClick={switchToRecovery}
                          className="text-ink-tertiary hover:text-emerald-400 text-sm transition-colors"
                        >
                          {isArabic ? "نسيت كلمة المرور؟" : "Forgot Password?"}
                        </button>
                      </div>
                    )}

                    {error && (
                      <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-center">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-on-fill rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading && <AnimatedHourglass size={18} />}
                      {mode === "signin"
                        ? isArabic ? "دخول" : "Sign In"
                        : isArabic ? "إنشاء حساب" : "Create Account"}
                    </button>
                  </form>
                </div>

                {/* Divider */}
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/[0.06]"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-slate-950 text-ink-tertiary text-xs uppercase tracking-widest font-medium">
                      {isArabic ? "أو جرب" : "Or Try"}
                    </span>
                  </div>
                </div>

                {/* Demo Personas */}
                <div className="space-y-3">
                  {personas.map((persona, index) => {
                    const Icon = persona.icon;
                    return (
                      <motion.button
                        key={persona.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + index * 0.1 }}
                        onClick={() => handleDemo(persona.id)}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className={`w-full py-4 px-5 bg-gradient-to-r ${persona.gradient} border ${persona.border} rounded-xl transition-all flex items-center gap-4 ${isArabic ? 'flex-row-reverse' : ''} backdrop-blur-sm`}
                      >
                        <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ${persona.iconColor}`}>
                          <Icon size={20} />
                        </div>
                        <div className={`flex-1 ${isArabic ? 'text-right' : 'text-left'}`}>
                          <div className="font-bold text-white text-base">
                            {isArabic ? persona.nameAr : persona.nameEn}
                          </div>
                          <div className="text-ink-secondary text-sm">
                            {isArabic ? persona.descAr : persona.descEn}
                          </div>
                        </div>
                        <div className="text-ink-tertiary">
                          <ArrowLeft size={18} className={isArabic ? '' : 'rotate-180'} />
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Install to Home Screen Instructions */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8"
                >
                  <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.04] rounded-xl p-4">
                    {/* Header */}
                    <div className={`flex items-center gap-2 mb-3 ${isArabic ? 'flex-row-reverse' : ''}`}>
                      <Smartphone size={14} className="text-ink-secondary" />
                      <span className="text-ink-secondary text-xs font-medium">
                        {isArabic ? 'أضف إلى الشاشة الرئيسية' : 'Add to Home Screen'}
                      </span>
                    </div>

                    {/* Tab Switcher */}
                    <div className="flex bg-white/[0.03] rounded-lg p-0.5 mb-3 border border-white/[0.04]">
                      <button
                        onClick={() => setInstallTab('iphone')}
                        className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                          installTab === 'iphone'
                            ? 'bg-white/[0.08] text-white'
                            : 'text-ink-tertiary hover:text-ink-secondary'
                        }`}
                      >
                        <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                        iPhone
                      </button>
                      <button
                        onClick={() => setInstallTab('android')}
                        className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                          installTab === 'android'
                            ? 'bg-white/[0.08] text-white'
                            : 'text-ink-tertiary hover:text-ink-secondary'
                        }`}
                      >
                        <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                          <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
                        </svg>
                        Android
                      </button>
                    </div>

                    {/* Instructions */}
                    <AnimatePresence mode="wait">
                      {installTab === 'iphone' ? (
                        <motion.div
                          key="iphone"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          className="space-y-2"
                        >
                          <div className={`flex items-center gap-2.5 ${isArabic ? 'flex-row-reverse' : ''}`}>
                            <div className="w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-ink-secondary font-bold shrink-0">1</div>
                            <div className={`flex items-center gap-1.5 text-ink-secondary text-xs ${isArabic ? 'flex-row-reverse' : ''}`}>
                              <span>{isArabic ? 'اضغط على' : 'Tap'}</span>
                              <Share size={12} className="text-emerald-400" />
                              <span>{isArabic ? 'في Safari' : 'in Safari'}</span>
                            </div>
                          </div>
                          <div className={`flex items-center gap-2.5 ${isArabic ? 'flex-row-reverse' : ''}`}>
                            <div className="w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-ink-secondary font-bold shrink-0">2</div>
                            <div className={`flex items-center gap-1.5 text-ink-secondary text-xs ${isArabic ? 'flex-row-reverse' : ''}`}>
                              <span>{isArabic ? 'اختر' : 'Select'}</span>
                              <Plus size={12} className="text-emerald-400" />
                              <span>{isArabic ? '"إضافة إلى الشاشة"' : '"Add to Home Screen"'}</span>
                            </div>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="android"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          className="space-y-2"
                        >
                          <div className={`flex items-center gap-2.5 ${isArabic ? 'flex-row-reverse' : ''}`}>
                            <div className="w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-ink-secondary font-bold shrink-0">1</div>
                            <div className={`flex items-center gap-1.5 text-ink-secondary text-xs ${isArabic ? 'flex-row-reverse' : ''}`}>
                              <span>{isArabic ? 'اضغط على' : 'Tap'}</span>
                              <MoreVertical size={12} className="text-emerald-400" />
                              <span>{isArabic ? 'في Chrome' : 'in Chrome'}</span>
                            </div>
                          </div>
                          <div className={`flex items-center gap-2.5 ${isArabic ? 'flex-row-reverse' : ''}`}>
                            <div className="w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-ink-secondary font-bold shrink-0">2</div>
                            <div className={`flex items-center gap-1.5 text-ink-secondary text-xs ${isArabic ? 'flex-row-reverse' : ''}`}>
                              <span>{isArabic ? 'اختر' : 'Select'}</span>
                              <Smartphone size={12} className="text-emerald-400" />
                              <span>{isArabic ? '"تثبيت التطبيق"' : '"Install App"'}</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer - Corporate + Easter Egg */}
      <footer className="py-8 relative z-10 border-t border-white/[0.03]">
        {/* Easter Egg Line */}
        <p 
          onClick={handleFooterClick}
          className="text-ink-tertiary text-xs font-medium tracking-wide select-none text-center mb-4"
        >
          {isArabic ? `صُنع بـ 💙 ${currentFooterEnding}` : `Built with 💙 for ${currentFooterEnding}`}
        </p>
        
        {/* Corporate Footer */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-ink-tertiary text-[10px] tracking-wide">
          <span>© Hseeb 2025</span>
          <span className="hidden sm:inline">•</span>
          <span>{isArabic ? 'صُنع في المدينة المنورة' : 'Made in AlMadinah'}</span>
          <span className="hidden sm:inline">•</span>
          <a href="mailto:Admin@hseeb.com" className="hover:text-ink-tertiary transition-colors">Admin@hseeb.com</a>
          <span className="hidden sm:inline">•</span>
          <span>v3.5</span>
          <span className="hidden sm:inline">•</span>
          {/* Hidden Admin Login Link */}
          <button
            onClick={() => navigate('/super-secret-admin')}
            className="hover:text-ink-tertiary transition-colors cursor-pointer"
          >
            {isArabic ? 'تسجيل دخول المشرف' : 'Admin Login'}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Login;